import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import ApiAiReport, {
  AiReportDetail,
  AiReportListItem,
  AiReportQuarter,
} from "@/api/ApiAiReport";

const LAST_VIEWED_KEY = "CCS_HOME_AI_REPORT_LAST_VIEWED";

interface LastViewedMap {
  // key: `${childId}:${businessYear}:${quarter}`
  [key: string]: string; // viewed ISO
}

function loadLastViewed(): LastViewedMap {
  try {
    const raw = localStorage.getItem(LAST_VIEWED_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as LastViewedMap;
  } catch {
    return {};
  }
}

function saveLastViewed(map: LastViewedMap) {
  try {
    localStorage.setItem(LAST_VIEWED_KEY, JSON.stringify(map));
  } catch {
    // localStorage が無効な環境は無視する
  }
}

function viewedKey(
  childId: number,
  businessYear: number,
  quarter: AiReportQuarter,
): string {
  return `${childId}:${businessYear}:${quarter}`;
}

export interface AiReportState {
  reports: AiReportListItem[];
  detail: AiReportDetail | null;
  lastViewed: LastViewedMap;
}

@Module({ dynamic: true, store, name: "ai-report", namespaced: true })
class AiReportModule extends VuexModule implements AiReportState {
  public reports: AiReportListItem[] = [];
  public detail: AiReportDetail | null = null;
  public lastViewed: LastViewedMap = loadLastViewed();

  @Mutation
  public setReports(value: AiReportListItem[]) {
    this.reports = value;
  }

  @Mutation
  public setDetail(value: AiReportDetail | null) {
    this.detail = value;
  }

  @Mutation
  public markViewedMutation({
    childId,
    businessYear,
    quarter,
    viewedAt,
  }: {
    childId: number;
    businessYear: number;
    quarter: AiReportQuarter;
    viewedAt: string;
  }) {
    const next = { ...this.lastViewed, [viewedKey(childId, businessYear, quarter)]: viewedAt };
    this.lastViewed = next;
    saveLastViewed(next);
  }

  /**
   * 子の公開済レポート一覧を取得する。
   */
  @Action({ rawError: true })
  public async fetchReports({ childId }: { childId: number }) {
    const response = await ApiAiReport.listPublished({ childId });
    this.setReports(response);
  }

  /**
   * 個別レポートを取得する。同時に既読として記録する。
   */
  @Action({ rawError: true })
  public async fetchDetail({
    childId,
    businessYear,
    quarter,
  }: {
    childId: number;
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const response = await ApiAiReport.getPublished({
      childId,
      businessYear,
      quarter,
    });
    this.setDetail(response);
    if (response) {
      this.markViewedMutation({
        childId,
        businessYear,
        quarter,
        viewedAt: new Date().toISOString(),
      });
    }
    return response;
  }

  /**
   * 指定レポートが未読か判定する。
   * `publishedAt > lastViewedAt` の場合は未読。
   */
  public get isUnread(): (item: AiReportListItem) => boolean {
    return (item: AiReportListItem) => {
      const key = viewedKey(item.childId, item.businessYear, item.quarter);
      const last = this.lastViewed[key];
      if (!last) return true;
      return new Date(item.publishedAt).getTime() > new Date(last).getTime();
    };
  }

  /**
   * 未読件数を返す。
   */
  public get unreadCount(): number {
    return this.reports.filter((r) => this.isUnread(r)).length;
  }
}

export const aiReportModule = getModule(AiReportModule);
